import { NeedDto, NeedsActionDto } from '@act/shared/data-transfer-objects';
import { Box, FormBuilder } from '@act/storybook';
import { Dialog, DialogContent, Typography } from '@mui/material';
import { TrackingProvider } from 'react-components/tracking';
import Loader from '@act/react-components/workflows/Loader';
import { GenericDialogProps } from 'utilities/dialog-context/DialogContextHelpers';
import CloseableDialogTitle from './components/dialog-components/CloseableDialogTitle';
import SubmittableDialogActions from './components/dialog-components/SubmittableDialogActions';
import { useAddActionDialogForm } from './hooks/useAddActionDialogForm.hook';

export interface AddActionDialogUniqueProps {
  need: NeedDto;
  action?: NeedsActionDto;
}

export type AddActionCallbackInput = null;
export type AddActionDialogProps = GenericDialogProps<null> &
  AddActionDialogUniqueProps;

export const AddActionDialog = ({
  need,
  action,
  open,
  setOpen,
}: AddActionDialogProps) => {
  const {
    showError,
    formConfig,
    control,
    formState,
    isProcessing,
    handleClose,
    handleSubmission,
    confirmDeleteAction,
    canDeleteNeedActions,
    isResourcingResearch,
  } = useAddActionDialogForm({ need, action, setOpen });

  const title = `${action ? 'Edit' : 'Add'} ${
    isResourcingResearch ? 'resource research' : 'action'
  }`;
  const subtitle = isResourcingResearch
    ? 'Record the resourcing activity'
    : 'Record the time and action performed';

  return (
    <TrackingProvider contextName="add-action-dialog">
      <Dialog open={open} onClose={handleClose} maxWidth="sm">
        <CloseableDialogTitle title={title} handleClose={handleClose} />
        <DialogContent>
          <Box padding="12px 24px 24px">
            {showError && (
              <Typography variant="labelMedium" color="error">
                An error has occurred.
              </Typography>
            )}
            <Box padding="16px 0">
              <Typography variant="titleMedium">{subtitle}</Typography>
            </Box>
            <Box borderBottom="1px solid #DCE4E8">{''}</Box>
            {isProcessing ? (
              <Loader />
            ) : (
              <FormBuilder
                config={formConfig}
                control={control}
                formState={formState}
                disabled={isProcessing}
              />
            )}
          </Box>
        </DialogContent>
        <SubmittableDialogActions
          submissionText="Save and close"
          isValid={formState.isValid}
          ignoreIsValid={true}
          disabled={isProcessing}
          handleClose={handleClose}
          handleSubmission={handleSubmission}
          handleDelete={
            (action && action?.canDelete) || canDeleteNeedActions
              ? confirmDeleteAction
              : null
          }
        />
      </Dialog>
    </TrackingProvider>
  );
};

export default AddActionDialog;
